/**
 * Baixa e lê UM arquivo diário do INPE (~3,6 MB): usado dentro do Web Worker
 * e, se o worker não estiver disponível, na thread principal.
 */
import { makeDailyAmazoniaTest } from './amazonia';
import { parseDailyCsv } from './csv';
import { fetchWithRetry, readTextWithProgress } from './net';
import { projectTable } from './project';
import type { FireTable } from './types';

export interface DailyFileRequest {
  url: string;
  /** 'no-cache' para revalidar o arquivo do dia corrente (cresce de hora em hora). */
  cache: RequestCache;
  /** Versão já em memória: se o servidor devolver a mesma, não lê de novo. */
  known?: { lastModified: string | null; length: string | null } | null;
  /** Códigos IBGE da Amazônia Legal (null = critério aproximado). */
  codes: number[] | null;
}

export type DailyFileResult =
  | { status: 'missing' }
  | { status: 'unchanged' }
  | {
      status: 'ok';
      table: FireTable;
      bytes: number;
      lastModified: string | null;
      length: string | null;
      read: number;
      invalid: number;
    };

export async function fetchDailyTable(
  req: DailyFileRequest,
  opts: { signal?: AbortSignal; onProgress?: (loaded: number, total: number) => void } = {},
): Promise<DailyFileResult> {
  const res = await fetchWithRetry(req.url, { signal: opts.signal, cache: req.cache, retries: 2 });
  if (!res) return { status: 'missing' };
  const lastModified = res.headers.get('last-modified');
  const length = res.headers.get('content-length');
  if (req.known && req.known.lastModified && req.known.lastModified === lastModified && req.known.length === length) {
    await res.body?.cancel().catch(() => {});
    return { status: 'unchanged' };
  }
  const { text, bytes } = await readTextWithProgress(res, opts.onProgress);
  const test = makeDailyAmazoniaTest(req.codes ? new Set(req.codes) : null);
  const { table, read, invalid } = parseDailyCsv(text, test);
  projectTable(table);
  return { status: 'ok', table, bytes, lastModified, length, read, invalid };
}
